import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  getSDEEvent,
  getRegistration,
  getEventRegistrations,
  getCandidaturesForDesigner,
  bulkUpsertCandidatures,
  deleteCandidature,
} from '../../Components/SDE24/sdeSupabaseService';
import { useNavigate } from 'react-router-dom';
import { DragDropPriorityRanking } from '../../Components/SDE24/UI/DragDropPriorityRanking';
import { MAX_CANDIDATURES } from '../../Components/SDE24/sdeConstants';

// ─── Styled ────────────────────────────────────────
const Page = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 2rem 1.5rem 4rem;
  min-height: 80vh;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  h1 { font-size: 1.75rem; font-weight: 700; color: #1F2937; margin-bottom: 0.25rem; }
  p { color: #6B7280; font-size: 0.95rem; max-width: 600px; margin: 0 auto; }
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  background: ${p => p.bg || '#F3F4F6'};
  color: ${p => p.fg || '#374151'};
`;

const SaveBar = styled.div`
  position: sticky;
  bottom: 0;
  background: white;
  border-top: 1px solid #E5E7EB;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 10;
  box-shadow: 0 -2px 8px rgba(0,0,0,0.06);
`;

const Btn = styled.button`
  padding: 0.65rem 1.5rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  background: ${p => p.variant === 'primary' ? '#2563EB' : '#F3F4F6'};
  color: ${p => p.variant === 'primary' ? 'white' : '#374151'};
  &:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const LoadingBox = styled.div`
  display: flex; align-items: center; justify-content: center;
  min-height: 60vh; font-size: 1.1rem; color: #6B7280;
`;

const ErrorBox = styled.div`
  max-width: 500px; margin: 4rem auto; text-align: center;
  padding: 2rem; background: #FEF2F2; border-radius: 12px; color: #B91C1C;
  h2 { margin: 0 0 0.5rem; font-size: 1.25rem; }
`;

// ─── Component ─────────────────────────────────────
const SDO_EVENT_ID = 'a0575e69-2e97-4ba3-a130-4f69c3a5d892';

const SuccessBar = styled.div`
  background: #D1FAE5;
  border: 1px solid #6EE7B7;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  span { color: #065F46; font-weight: 600; font-size: 0.95rem; }
`;

const ReturnBtn = styled.button`
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border: 2px solid #065F46;
  background: white;
  color: #065F46;
  transition: all 0.2s;
  &:hover { background: #065F46; color: white; }
`;

const ClosedBanner = styled.div`
  max-width: 700px;
  margin: 0 auto 1.5rem;
  padding: 1.25rem 1.5rem;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 12px;
  text-align: center;
  color: #991B1B;
  font-weight: 600;
  font-size: 1rem;
`;

const AdminBar = styled.div`
  background: #FFEDD5; border: 1px solid #FB923C; border-radius: 8px; padding: 0.75rem 1rem;
  display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; margin-bottom: 1.5rem; flex-wrap: wrap;
  color: #9A3412; font-size: 0.9rem; font-weight: 600;
  button { background: white; border: 1px solid #FB923C; color: #9A3412; border-radius: 6px; padding: 0.35rem 0.75rem; font-size: 0.8rem; font-weight: 600; cursor: pointer; }
`;

const CANDIDATURES_CLOSED = true;

const CandidaturesNew = ({ fullUser }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language === 'fr' ? 'fr' : 'en';
  const eventId = searchParams.get('eid');
  // Admin "act as" mode: edit candidatures on behalf of another designer, bypassing the closure
  const actAs = (fullUser?.user?.role === 'admin' && searchParams.get('as')) ? searchParams.get('as').trim() : null;
  const targetEmail = actAs || fullUser?.user?.email;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [event, setEvent] = useState(null);
  const [myRegistration, setMyRegistration] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loadedCandidatureIds, setLoadedCandidatureIds] = useState([]);
  const candidaturesClosed = CANDIDATURES_CLOSED && !actAs;

  // ─── Load data ────────────────────────────────
  useEffect(() => {
    if (!eventId || !targetEmail) return;
    load();
    // eslint-disable-next-line
  }, [eventId, targetEmail]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Fetch event
      const { data: ev, error: evErr } = await getSDEEvent(eventId);
      if (evErr || !ev) { setError("Événement introuvable."); setLoading(false); return; }
      setEvent(ev);

      // 2. Fetch my Pop B registration (try login email, then HubSpot email)
      let { data: reg, error: regErr } = await getRegistration(eventId, targetEmail, 'pop_b');
      if (!actAs && (!reg || regErr) && fullUser.hsProfile?.email && fullUser.hsProfile.email !== fullUser.user.email) {
        ({ data: reg, error: regErr } = await getRegistration(eventId, fullUser.hsProfile.email, 'pop_b'));
      }
      if (regErr || !reg) {
        setError(actAs
          ? `Aucune inscription Pop B trouvée pour ${actAs}.`
          : "Inscription Pop B introuvable. Veuillez d'abord vous inscrire en tant que designer.");
        setLoading(false);
        return;
      }
      if (reg.status !== 'submitted' && reg.status !== 'validated') {
        navigate(`/evenements/sdo/register?eid=${eventId}&pop=b${actAs ? `&as=${encodeURIComponent(actAs)}` : ''}`);
        return;
      }
      setMyRegistration(reg);

      // 3. Fetch validated Pop A registrations
      const { data: popARegs } = await getEventRegistrations(eventId, 'pop_a');
      const validatedPopA = (popARegs || []).filter(r => r.status === 'validated');
      const mapped = validatedPopA.map(r => {
        const meta = r.metadata || {};
        return {
          id: r.id,
          name: r.company_name || 'Entreprise',
          location: meta.website || '',
          description: meta.offre || meta.savoir_faire || '',
          categories: [
            ...(r.artisanat ? ['artisanat'] : []),
            ...(r.mobilier ? ['mobilier'] : []),
          ],
          specialties: r.specialties || [],
          createdAt: r.created_at,
          validatedAt: r.updated_at,
          _raw: r,
        };
      }).sort((a, b) => a.name.localeCompare(b.name, 'fr'));
      setCompanies(mapped);

      // 4. Fetch existing candidatures for this designer
      const { data: existingCands } = await getCandidaturesForDesigner(eventId, reg.id);
      if (existingCands && existingCands.length > 0) {
        setLoadedCandidatureIds(existingCands.map(c => ({ id: c.id, companyId: c.company_registration_id })));
        const restored = existingCands
          .sort((a, b) => a.priority - b.priority)
          .map(c => {
            const companyReg = mapped.find(m => m.id === c.company_registration_id);
            return companyReg ? { ...companyReg, priority: c.priority, _candidatureId: c.id } : null;
          })
          .filter(Boolean);
        setSelectedCompanies(restored);
      }
    } catch (err) {
      console.error(err);
      setError("Une erreur est survenue lors du chargement.");
    }
    setLoading(false);
  };

  // ─── Handle ranking updates from DragDrop ─────
  const handleUpdate = useCallback((updated) => {
    setSelectedCompanies(updated);
    setDirty(true);
  }, []);

  // ─── Save candidatures ────────────────────────
  const handleSave = async () => {
    if (!myRegistration || selectedCompanies.length === 0) return;
    setSaving(true);
    try {
      // Delete candidatures that were removed from the selection
      const currentCompanyIds = new Set(selectedCompanies.map(c => c.id));
      const toDelete = loadedCandidatureIds.filter(c => !currentCompanyIds.has(c.companyId));
      for (const c of toDelete) {
        await deleteCandidature(c.id);
      }

      // Existing rows get only their priority updated so an admin-confirmed status is never reset to pending
      const knownCompanyIds = new Set(loadedCandidatureIds.map(c => c.companyId));
      const rows = selectedCompanies.map((c, idx) => ({
        event_id: eventId,
        designer_registration_id: myRegistration.id,
        company_registration_id: c.id,
        priority: idx + 1,
      }));
      const existingRows = rows.filter(r => knownCompanyIds.has(r.company_registration_id));
      const newRows = rows.filter(r => !knownCompanyIds.has(r.company_registration_id)).map(r => ({ ...r, status: 'pending' }));

      const results = await Promise.all([
        existingRows.length ? bulkUpsertCandidatures(existingRows) : { data: [] },
        newRows.length ? bulkUpsertCandidatures(newRows) : { data: [] },
      ]);
      const upsertErr = results.find(r => r.error)?.error;
      if (upsertErr) {
        console.error('Save error:', upsertErr);
        alert("Erreur lors de la sauvegarde: " + upsertErr.message);
      } else {
        const saved = results.flatMap(r => r.data || []);
        setLoadedCandidatureIds(saved.map(c => ({ id: c.id, companyId: c.company_registration_id })));
        setDirty(false);
        setLastSaved(new Date());
        setSaveSuccess(true);
      }
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la sauvegarde.");
    }
    setSaving(false);
  };

  // ─── Render ───────────────────────────────────
  if (loading) {
    return <LoadingBox>Chargement des candidatures...</LoadingBox>;
  }

  if (error) {
    return (
      <ErrorBox>
        <h2>Candidatures</h2>
        <p>{error}</p>
      </ErrorBox>
    );
  }

  return (
    <Page>
      {actAs && (
        <AdminBar>
          <span>Mode admin — vous modifiez les candidatures de {actAs}</span>
          <button onClick={() => navigate(`/admin/events/sdo?eid=${eventId}`)}>← Retour admin</button>
        </AdminBar>
      )}

      <Header>
        <h1>{event?.title_fr || 'Speed Dating'} — Candidatures</h1>
        <p>
          Sélectionnez les entreprises que vous souhaitez rencontrer et ordonnez-les par priorité (glisser-déposer).
          D'autres entreprises seront ajoutées jusqu'au 4 septembre prochain.
        </p>
      </Header>

      <StatusBar>
        <StatusBadge bg="#DBEAFE" fg="#1E40AF">
          {companies.length} entreprises disponibles
        </StatusBadge>
        <StatusBadge bg="#D1FAE5" fg="#065F46">
          {selectedCompanies.length}/{MAX_CANDIDATURES} sélectionnées
        </StatusBadge>
        {lastSaved && (
          <StatusBadge bg="#F0FDF4" fg="#166534">
            Sauvegardé {lastSaved.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </StatusBadge>
        )}
        {dirty && (
          <StatusBadge bg="#FEF3C7" fg="#92400E">
            Modifications non sauvegardées
          </StatusBadge>
        )}
      </StatusBar>

      {candidaturesClosed && (
        <ClosedBanner>
          Les candidatures sont désormais closes. Vous pouvez consulter vos sélections mais ne pouvez plus les modifier.
        </ClosedBanner>
      )}

      {saveSuccess && (
        <SuccessBar>
          <span>Vos candidatures ont bien été sauvegardées !</span>
          <ReturnBtn onClick={() => navigate(actAs ? `/admin/events/sdo?eid=${eventId}` : '/evenements/sdo2026')}>
            {actAs ? 'Retour admin' : 'Retour page événement'}
          </ReturnBtn>
        </SuccessBar>
      )}

      <DragDropPriorityRanking
        availableCompanies={companies}
        selectedCompanies={selectedCompanies}
        onUpdate={handleUpdate}
        maxSelections={event?.sde_config?.max_candidatures || MAX_CANDIDATURES}
      />

      <SaveBar>
        <span style={{ fontSize: '0.85rem', color: '#6B7280' }}>
          {selectedCompanies.length === 0
            ? "Cliquez sur les entreprises pour les ajouter à votre sélection"
            : `${selectedCompanies.length} entreprise${selectedCompanies.length > 1 ? 's' : ''} sélectionnée${selectedCompanies.length > 1 ? 's' : ''}`}
        </span>
        <Btn
          variant="primary"
          onClick={handleSave}
          disabled={saving || !dirty || selectedCompanies.length === 0 || candidaturesClosed}
        >
          {saving ? 'Sauvegarde...' : 'Sauvegarder mes candidatures'}
        </Btn>
      </SaveBar>
    </Page>
  );
};

export default CandidaturesNew;
